import Axhelper from '@/lib/Axhelper'

class ExpensesService {
	constructor() {
		this.baseUrl = window.RUNTIME_CONFIG.API_URL
		this.endpoints = {
			expenses: `/api/expenses/filters`,
			expensesDetails: `/api/expenses/details`,
			expensesStats: `/api/expenses/stats`,
			expensesTags: `/api/expenses/tags`,
			expensesDeleteOne: `/api/expenses/`,
			expensesDeleteMany: `/api/expenses/deletemany`,
			expensesCreate: `/api/expenses/`,
			expensesUpdate: `/api/expenses/`,
		}
	}

	async getExpenseById(expenseId) {
		try {
			if (!expenseId) {
				return { data: null, error: 'Invalid expense ID provided' }
			}

			const url = `${this.endpoints.expensesDetails}/${encodeURIComponent(expenseId)}`
			const response = await Axhelper.sendGetAsync(url)
			if (response.error) {
				return { data: null, error: response.error }
			}

			return { data: response.data || null, error: null }
		} catch (error) {
			console.error('ExpensesService.getExpenseById error:', error)
			return { data: null, error: error.message || 'Failed to fetch expense' }
		}
	}

	async getExpensesWithFilters(options = {}) {
		try {
			const { limit = 50, sortColumn = 'expenseDate', sortDirection = 'desc', filters = {} } = options
			const url = this._buildFilteredUrl({ limit, sortColumn, sortDirection, filters })
			const response = await Axhelper.sendGetAsync(url)

			if (response.error) {
				return { data: [], error: response.error, totalCount: 0, hasMore: false }
			}

			const totalCount = response.headers?.['x-total-count']
				? parseInt(response.headers['x-total-count'], 10)
				: response.data?.length || 0
			const hasMore = response.data?.length < totalCount

			return {
				data: response.data || [],
				error: null,
				totalCount,
				hasMore,
				headers: response.headers,
			}
		} catch (error) {
			console.error('ExpensesService.getExpensesWithFilters error:', error)
			return { data: [], error: error.message || 'Failed to fetch expenses', totalCount: 0, hasMore: false }
		}
	}

	async getExpenseStats() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.expensesStats)
			if (response.error) {
				return { data: {}, error: response.error }
			}
			return { data: response.data || {}, error: null }
		} catch (error) {
			console.error('ExpensesService.getExpenseStats error:', error)
			return { data: {}, error: error.message || 'Failed to fetch expense stats' }
		}
	}

	async getTags() {
		try {
			const response = await Axhelper.sendGetAsync(this.endpoints.expensesTags)
			if (response.error) {
				return { data: [], error: response.error }
			}
			const tags = Array.isArray(response.data) ? response.data : response.data?.tags || []
			return { data: tags, error: null }
		} catch (error) {
			console.error('ExpensesService.getTags error:', error)
			return { data: [], error: error.message || 'Failed to fetch tags' }
		}
	}

	async deleteExpenses(expenseIds) {
		try {
			const ids = [...new Set((expenseIds || []).map(id => String(id)).filter(Boolean))]
			if (!ids.length) {
				return { success: false, error: 'Invalid expense IDs provided', message: null }
			}

			if (ids.length === 1) {
				const url = `${this.endpoints.expensesDeleteOne}${ids[0]}`
				const res = await Axhelper.sendDeleteAsync(url)
				if (res?.error) return { success: false, error: res.error, message: null }
				return { success: true, error: null, message: 'Expense deleted successfully.' }
			}

			try {
				const bulkJson = await Axhelper.sendPostAsync(this.endpoints.expensesDeleteMany, { ids })
				if (!bulkJson?.error) {
					return { success: true, error: null, message: 'Selected expenses deleted successfully.' }
				}
				console.warn('[ExpensesService] bulk JSON delete failed:', bulkJson.error)
			} catch (error) {
				console.warn('[ExpensesService] bulk JSON delete threw:', error)
			}

			const results = await Promise.allSettled(
				ids.map(id => Axhelper.sendDeleteAsync(`${this.endpoints.expensesDeleteOne}${id}`))
			)
			const failed = results
				.map((r, index) => (r.status === 'fulfilled' && !r.value?.error ? null : ids[index]))
				.filter(Boolean)

			if (failed.length) {
				return { success: false, error: `Failed to delete ids: ${failed.join(', ')}`, message: null }
			}

			return { success: true, error: null, message: 'Selected expenses deleted successfully.' }
		} catch (error) {
			console.error('ExpensesService.deleteExpenses error:', error)
			return { success: false, error: error.message || 'Failed to delete expenses', message: null }
		}
	}

	async createExpense(expenseData) {
		try {
			const response = await Axhelper.sendPostAsync(this.endpoints.expensesCreate, expenseData)
			if (response.error) {
				return { success: false, error: response.error }
			}
			return { success: true, error: null, data: response.data || null }
		} catch (error) {
			console.error('ExpensesService.createExpense error:', error)
			return { success: false, error: error.message || 'Failed to create expense' }
		}
	}

	async updateExpense(expenseId, expenseData) {
		try {
			if (!expenseId) {
				return { success: false, error: 'Invalid expense ID provided' }
			}

			const url = `${this.endpoints.expensesUpdate}/${encodeURIComponent(expenseId)}`
			const response = await Axhelper.sendPutAsync(url, expenseData)
			if (response.error) {
				return { success: false, error: response.error }
			}
			return { success: true, error: null, data: response.data || null }
		} catch (error) {
			console.error('ExpensesService.updateExpense error:', error)
			return { success: false, error: error.message || 'Failed to update expense' }
		}
	}

	_buildFilteredUrl({ limit, sortColumn, sortDirection, filters }) {
		const params = [`limit=${limit}`, `sortcol=${sortColumn}`, `sortdir=${sortDirection}`]

		if (filters.searchText && filters.searchText.trim().length >= 2) {
			params.push(`searchText=${encodeURIComponent(filters.searchText.trim())}`)
		}

		if (filters.status) {
			params.push(`status=${encodeURIComponent(filters.status)}`)
		}

		if (filters.paymentMethod) {
			params.push(`paymentMethod=${encodeURIComponent(filters.paymentMethod)}`)
		}

		if (Array.isArray(filters.tags) && filters.tags.length) {
			filters.tags.forEach(tag => {
				params.push(`tags[]=${encodeURIComponent(tag)}`)
			})
		} else if (filters.tag) {
			// backward compatibility if a single tag is passed
			params.push(`tags[]=${encodeURIComponent(filters.tag)}`)
		}

		if (typeof filters.forVat === 'boolean') {
			params.push(`forVat=${filters.forVat}`)
		}

		if (typeof filters.forCorporateTax === 'boolean') {
			params.push(`forCorporateTax=${filters.forCorporateTax}`)
		}

		if (filters.fromDate) {
			params.push(`fromDate=${encodeURIComponent(filters.fromDate)}`)
		}

		if (filters.toDate) {
			params.push(`toDate=${encodeURIComponent(filters.toDate)}`)
		}

		return `${this.endpoints.expenses}?${params.join('&')}`
	}
}

export default new ExpensesService()
